import { css } from "@emotion/css";
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ReactComponent as CancelIcon } from "../../assets/images/ralvie/Close.svg";
import { ReactComponent as ErrorIcon } from "../../assets/images/ralvie/error-icon.svg";
import { ReactComponent as TagInactiveIcon } from "../../assets/images/ralvie/tag-icon-inactive.svg";
import CustomButton from "../../components/CustomButton";
import CustomSwitch from "../../components/CustomSwitch";
import CustomTextField from "../../components/DescriptionBox";
import { updateEvent } from "../../services/EventService";
import { findProjectsByAssignedMember } from "../../services/ProjectService";
import { updateTimesheet } from "../../services/WorklogService";
import CommonUtil from "../../util/CommonUtils";
import { TimesheetContext } from "../../util/context";
import CancelDialog from "../project/CancelDialog";
import EventList from "./EventList";
import "./LogWork.scss";
import AppContext from "../../config/AppContext";
import { getTasksByProjectId } from "../../services/TaskService";
import CustomTypography from "../../components/CustomTypography";
import TaskPopup from "../timesheet/TaskPopup";
import ProjectTagTextField from "../project/ProjectTagfield";
import { createTagWithProject } from "../../services/TagService";

function areEventListsIdentical(list1, list2) {
  // Check if the lengths of both lists are the same
  if (list1.length !== list2.length) {
    return false;
  }

  // Sort the lists by id (UUIDs are treated as strings)
  const sortedList1 = [...list1].sort((a, b) => a.id.localeCompare(b.id));
  const sortedList2 = [...list2].sort((a, b) => a.id.localeCompare(b.id));

  // Compare each object's id in both lists
  for (let i = 0; i < sortedList1.length; i++) {
    if (sortedList1[i].id !== sortedList2[i].id) {
      return false;
    }
  }

  return true;
}

export default function EditWorkLog(props) {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [projects, setProjects] = useState([]);
  const [totalEventsDuration, setTotalEventsDuration] = useState(null);
  const [totalEventsDurationInSec, setTotalEventsDurationInSec] = useState(0);
  const [toggleSubEvents, setToggleSubEvents] = useState(null);
  const [toggleSubEventsDuration, setToggleSubEventsDuration] = useState(0);
  const [eventHovered, setEventHovered] = useState(null);
  const [selectedEvents, setSelectedEvents] = useState([]);
  const selectedEventsRef = useRef(selectedEvents);
  const [totalEvents, setTotalEvents] = useState([]);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [isEventsModified, setIsEventsModified] = useState(false);
  const [eventCancelDialogOpen, setEventCancelDialogOpen] = useState(false);
  const [isModified, setIsModified] = useState(false);
  const [isProjectRemoved, setIsProjectRemoved] = useState(false);
  const {
    setEventsToLog,
    setReload,
    setTimesheetToEdit,
    setTimesheetEvents,
    timesheetEvents,
    setLogSubmited,
    subEventSelected, SetSubEventSelected, loggedEventsIds,
    unSelectedEvents, setUnSelectedEvents
  } = useContext(TimesheetContext);

  const [isSubmit, setIsSubmit] = useState(false);
  const [eventToEdit, setEventToEdit] = useState(null);
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [tagError, setTagError] = useState(false);
  const [durationModified, setDurationModified] = useState(false);
  const { mode } = useContext(AppContext);
  const unSelectedEventsRef = useRef(unSelectedEvents);
  const [taskList, setTaskList] = useState([]);
  const [taskId, setTaskId] = useState("");
  const [isTaskPopupOpen, setIsTaskPopupOpen] = useState(false);
  const [showTagInput, setShowTagInput] = useState(false);
  const [tagData, setTagData] = useState([]);
  const [isCreatingTag, setIsCreatingTag] = useState(false);
  const [isCreatingTask, setIsCreatingTask] = useState(false);
  const [archivedProjectTask, setArchivedProjectTask] = useState([]);

  const [state, setState] = useState({
    id: "",
    title: "",
    projectId: "",
    tags: [],
    description: "",
    loggedDuration: 0,
    eventDuration: 0,
    events: [],
    hrs: 0,
    mins: 0,
    billable: false,
  });

  const [error, setError] = useState({
    title: "",
    projectId: "",
    tags: "",
    description: "",
    loggedDuration: "",
    eventDuration: "",
    events: "",
    taskId: "",
  });

  useEffect(() => {
    if (unSelectedEvents?.length > 0) {
      let totalEvents = 0;
      selectedEventsRef?.current?.forEach((event) => {
        totalEvents = totalEvents + event?.events?.length;
      });
      let updatedTotalEvents = totalEvents - unSelectedEvents?.length;
      setTotalEvents(updatedTotalEvents);
    } else {
      let totalEvents = 0;
      selectedEventsRef?.current?.forEach((event) => {
        totalEvents = totalEvents + event?.events?.length;
      });
      setTotalEvents(totalEvents);
    }
  }, [unSelectedEvents]);

  function groupEventsByApp(events) {
    let groupedEvents = [];

    events.forEach((event) => {
      const existingGroup = groupedEvents.find(
        (group) =>
          ((group.applicationName &&
            event.applicationName &&
            group.applicationName === event.applicationName) ||
            group.application?.name === event.application?.name ||
            group.application?.applicationInfo?.name ===
            event.application?.name) &&
          group.data.app !== "afk"
      );

      if (existingGroup) {
        const start = existingGroup.start
          ? new Date(Math.min(existingGroup.start, event.start))
          : event.start;
        const end = existingGroup.end
          ? new Date(Math.max(existingGroup.end, event.end))
          : event.end;
        if (existingGroup.duration < event.duration) {
          existingGroup.id = event.id;
          existingGroup.timestamp = event.timestamp;
          existingGroup.duration = event.duration;
          existingGroup.data = event.data;
          existingGroup.event_id = event.event_id;
          existingGroup.title = event.title;
          existingGroup.start = start;
          existingGroup.end = end;
          existingGroup.projectId = event.projectId;
        }
        existingGroup.events.push({
          ...event,
          id: event.id,
          timestamp: event.timestamp,
          duration: event.duration,
          data: event.data,
          start: event.start,
          end: event.end,
          event_id: event.event_id,
          title: event.title,
        });
      } else {
        groupedEvents.push({
          ...event,
          events: [event],
          start: event.start,
          end: event.end,
        });
      }
    });
    groupedEvents = groupedEvents.filter((event) => event.duration >= 30);
    return groupedEvents;
  }

  const [newTag, setNewTag] = useState({
    name: "",
    color: "",
  });
  const resetOnAdd = () => {
    setNewTag({
      name: "",
      color: "",
    });
    setError("");
    setShowTagInput(false);
  };

  const handleAddTag = () => {
    if (isCreatingTag) return;
    setIsCreatingTag(true);
    const payload = {
      name: newTag.name,
      iconUrl: newTag.color,
      projectId: selectedProject?.id
    };

    // const projectId = selectedProject?.id

    createTagWithProject(selectedProject?.id, payload).then((res) => {
      if (res.data.code === "RCI0022") {
        toast.success(t("RAL0197"));
        const newTagObject = {
          id: res.data.data.id,
          name: newTag.name,
          iconUrl: payload.iconUrl
        };
        const updatedProject = {
          ...selectedProject,
          tags: [...(selectedProject.tags || []), newTagObject]
        };

        setProjects(prevProjects =>
          prevProjects.map(p =>
            p.id === selectedProject.id ? updatedProject : p
          )
        );

        setSelectedProject(updatedProject);
        setSelectedTags(prev => [...prev, newTagObject]);

        resetOnAdd();
        return;
      }
      throw new Error(res.data.message);
    })
      .catch(error => {
        toast.error(error.message);
      })
      .finally(() => {
        setIsCreatingTag(false);
      });
  };
  const handleTaskCreated = (newTask) => {
    setIsCreatingTask(true);
    if (newTask.projectId === selectedProject?.id) {
      setTaskList(prevTasks => [...prevTasks, newTask]);
      setTaskId(newTask.id);
      setIsModified(true);
      setError({ ...error, taskId: "" });
    }
    getTasksByProjectId(selectedProject.id).then((res) => {
      if (res.status === 200 && res.data?.code === "RCI0000") {
        const updatedTasks = res.data?.data?.tasks?.sort((o1, o2) => o1.taskId - o2.taskId) || [];
        if (archivedProjectTask.status === "ARCHIVED" || archivedProjectTask.status === "COMPLETED") {
          setTaskList([...updatedTasks, archivedProjectTask] || []);
        } else
          setTaskList(updatedTasks || []);

        const taskExists = updatedTasks?.some(t => t?.id === newTask?.id);

        if (taskExists) {
          setTaskId(newTask.id);
          setIsModified(true);
        } else {
          setIsModified(false);
        }
        setError({ ...error, taskId: "" });
      }
    })
      .finally(() => {
        setIsCreatingTask(false);
      });
  };
  const handleCloseTaskPopup = () => {
    setIsTaskPopupOpen(false);
  };
  const handleAddChange = (e) => {
    const value = e.target.value;
    setNewTag({ ...newTag, name: value });
    setError("");
  };

  const commonComponentsProps = (mode) => ({
    popper: {
      modifiers: [
        {
          name: "preventOverflow",
          options: {
            boundary: "viewport",
          },
        },
      ],
    },
    paper: {
      sx: {
        "& .MuiAutocomplete-listbox .MuiAutocomplete-option": {
          "&:hover": {
            backgroundColor: mode === "dark" ? "#362C20" : "#FFF9F2",
          },
          "&.Mui-selected": {
            backgroundColor: mode === "dark" ? "#362C20" : "#FFF9F2",
            color: "#FA9C2B",
          },
          "&.Mui-selected:hover": {
            backgroundColor: mode === "dark" ? "#362C20" : "#FFF9F2",
            color: "#FA9C2B",
          },
        },
      },
    },
  });

  useEffect(() => {
    if (props.timesheetToEdit) {
      let timesheeet = props.timesheetToEdit;
      let duration = CommonUtil.getDurationWithoutRoundoff(
        timesheeet.loggedDuration
      );
      setState({
        ...state,
        id: timesheeet?.id,
        title: timesheeet?.title,
        description: timesheeet?.description,
        projectId: timesheeet?.project?.id,
        tags: timesheeet?.tags,
        billable: timesheeet?.billable,
        hrs: duration.hr,
        mins: duration.min,
      });
      setArchivedProjectTask(timesheeet?.task)
      setTaskId(timesheeet?.task?.id || "")
    } else {
      reset();
    }
  }, [props.timesheetToEdit]);

  useEffect(() => {
    // console.log("useEffect selectedEvents", selectedEvents)
    let events = selectedEvents;
    let totalDuration = 0;
    if (events && events instanceof Array) {
      let totalEvents = [];
      events.forEach((e) => {
        totalEvents.push(...e?.events);
        const filterSelectedEvent = unSelectedEvents?.length > 0 ?
          e?.events?.filter(et => !unSelectedEvents?.includes(et.id)) :
          e?.events;
        // console.log("filterSelectedEvent", filterSelectedEvent);
        let duration = filterSelectedEvent?.reduce((accumulator, currentValue) => {
          return accumulator + currentValue?.duration;
        }, 0);
        if (!duration) duration = e.duration;
        totalDuration += duration;
      });
      const result = totalEvents
        ?.map((e) => e.id)
        .filter((element) => !unSelectedEventsRef?.current?.includes(element));
      // console.log("result?.length", result?.length);
      setTotalEvents(result?.length);
      setTotalEventsDuration(
        CommonUtil.getDurationWithoutRoundoff(totalDuration)
      );
      setTotalEventsDurationInSec(totalDuration);
    } else {
      setTotalEventsDuration(null);
      setTotalEventsDurationInSec(0);
    }
  }, [selectedEvents]);

  useEffect(() => {
    if (totalEventsDurationInSec > 0) {
      let duration = CommonUtil.getDurationWithoutRoundoff(
        totalEventsDurationInSec
      );
      setTotalEventsDuration(duration);
      // setState({ ...state, hrs: duration?.hr, mins: duration.min });
    } else {
      setTotalEventsDuration(null);
      // setState({ ...state, hrs: 0, mins: 0 });
    }
  }, [totalEventsDurationInSec]);

  useEffect(() => {
    if (
      props.timesheetToEdit &&
      totalEventsDuration &&
      (isEventsModified ||
        !areEventListsIdentical(props.timesheetToEdit?.events, timesheetEvents))
    ) {
      let timesheeet = props.timesheetToEdit;
      setIsModified(true);
      let min = 0;
      if (totalEventsDuration?.hr === 0 && totalEventsDuration?.min === 0) {
        min = 1;
      } else {
        min = totalEventsDuration?.min;
      }
      setState({
        ...state,
        id: timesheeet?.id,
        title: timesheeet?.title,
        description: timesheeet?.description,
        projectId: timesheeet?.project?.id,
        tags: timesheeet?.tags,
        billable: timesheeet?.billable,
        hrs: totalEventsDuration.hr,
        mins: min,
      });
      setTaskId(timesheeet?.task?.id || "");
      setIsEventsModified(true);
    }
  }, [totalEventsDuration]);

  useEffect(() => {
    findProjectsByAssignedMember(CommonUtil.getLoggedUser()?.id)
      .then((res) => {
        if (res?.data && Array.isArray(res?.data?.data)) {
          const currentDate = new Date();
          currentDate.setHours(0, 0, 0, 0);
          const filteredProjects = res?.data?.data?.filter(
            (project) => project && 
            project.status !== "ARCHIVED" && 
            project.status !== "COMPLETED" &&
            new Date(project.startDate).setHours(0, 0, 0, 0) <= currentDate.getTime()
          );
          setProjects(filteredProjects);
        } else {
          setProjects([]);
        }
      })
      .catch((error) => {
        console.error("Error fetching projects:", error);
      });
  }, []);
  const tagSelected = (event, value) => {
    setIsModified(true);
    if (value?.length > 20) {
      setError({
        ...error,
        tags: t("RAL0579"),
      });
      setTimeout(() => {
        setError({
          ...error,
          tags: "",
        });
      }, 2000);
      return;
    }
    setSelectedTags(value);
    setError({ ...error, tags: "" });
  };

  const reset = () => {
    setEventToEdit(null);
    setState({
      projectId: "",
      tags: [],
      description: "",
      loggedDuration: 0,
      eventDuration: 0,
      events: [],
      hrs: 0,
      mins: 0,
    });
    setSelectedTags([]);
    setSelectedProject(null);
  };

  useEffect(() => {
    if (state.projectId) {
      let project = projects?.filter((p) => p?.id === state.projectId);
      if (project.length > 0) {
        setSelectedProject(project[0]);
        setIsProjectRemoved(false);
      } else {
        setSelectedProject(props?.timesheetToEdit?.project);
        setIsProjectRemoved(true);
      }
    }
  }, [state.projectId, projects]);

  useEffect(() => {
    setNewTag({
      name: "",
      color: "",
    });
    setShowTagInput(false);
  }, [selectedProject]);

  useEffect(() => {
    if (selectedProject && selectedProject?.id) {
      getTasksByProjectId(selectedProject?.id).then((res) => {
        if (res.status === 200 && res.data?.code === "RCI0000") {
          const tl = res.data?.data?.tasks?.sort((o1, o2) => o1.taskId - o2.taskId);
          if (archivedProjectTask.status === "ARCHIVED" || archivedProjectTask.status === "COMPLETED") {
            setTaskList([...tl, archivedProjectTask] || []);
          } else
            setTaskList(tl || []);
        } else {
          setTaskList([]);
        }
      })
    }
  }, [selectedProject]);

  useEffect(() => {
    if (state.tags) {
      setSelectedTags(state.tags);
    }
  }, [state.tags]);

  useEffect(() => {
    let isValid = isValidForm();
    if (isModified || durationModified || isEventsModified || isValid) {
      setIsSubmit(true);
    } else {
      setIsSubmit(isValid);
    }
  }, [state, selectedTags, selectedEvents, taskId, isModified, durationModified, isEventsModified]);

  useEffect(() => {
    if (durationModified && totalEventsDuration) {
      let min = 0;
      if (totalEventsDuration?.hr === 0 && totalEventsDuration?.min === 0) {
        min = 1;
      } else {
        min = totalEventsDuration?.min;
      }
      setState({
        ...state,
        hrs: totalEventsDuration?.hr,
        mins: min,
      });
    }
  }, [totalEventsDuration]);

  const handleEventUpdate = (parent) => {
    if (!eventToEdit?.title) {
      toast.error(t("RAL0580"), {
        duration: 2000,
      });
      return;
    }
    updateEvent(eventToEdit?.id, { windowTitle: eventToEdit.title }).then(
      (res) => {
        if (res.status === 200) {
          let events = selectedEvents.filter((e) => e.id !== parent?.id);
          if (parent?.events?.length > 0) {
            let subEvents = parent?.events?.filter(
              (e) => e.id !== eventToEdit?.id
            );
            subEvents.push(eventToEdit);
            if (events.length === 0 || parent.id === eventToEdit?.id) {
              let updatedParent = { ...parent, title: eventToEdit.title };

              let updatedEvent = { ...updatedParent, events: subEvents };
              events.push(updatedEvent);
            } else {
              let updatedEvent = { ...parent, events: subEvents };
              events.push(updatedEvent);
            }
          } else {
            events.push(eventToEdit);
          }
          selectedEventsRef.current = events;
          setSelectedEvents(events);
          setEventToEdit(null);
        }
      }
    );
  };

  useEffect(() => {
    let events = timesheetEvents;
    if (events && events instanceof Array && events.length > 0) {
      let groupedEvents = groupEventsByApp(timesheetEvents);
      selectedEventsRef.current = groupedEvents;
      setSelectedEvents(groupedEvents);
    } else {
      setTotalEventsDuration(null);
      setTotalEventsDurationInSec(0);
      selectedEventsRef.current = [];
      setSelectedEvents([]);
    }
    if (subEventSelected?.length === 0) {
      let ids = events?.map(ev => ev.id);
      SetSubEventSelected(ids)
    }
  }, [timesheetEvents?.length]);

  const handleUnselectEvent = (event, parent) => {

    setDurationModified(true);
    setIsEventsModified(true);
    let id = event?.id;
    let isIdExist = unSelectedEvents.find((e) => e === id);
    if (isIdExist) {
      let events = unSelectedEvents.filter((e) => e != id);
      unSelectedEventsRef.current = events;
      setUnSelectedEvents(events);
      let duration = totalEventsDurationInSec + Number(event?.duration);
      setTotalEventsDurationInSec(duration);
      setToggleSubEventsDuration(
        toggleSubEventsDuration + Number(event?.duration)
      );
    } else {
      let events = [...unSelectedEvents, id];
      let parentEvents = parent.events.map((e) => e.id);

      if (parentEvents.every((element) => events.includes(element))) {
        let updatedEventsToLog = selectedEvents.filter(
          (e) => e.id !== parent.id
        );
        if (updatedEventsToLog.length < 1) {
          setEventCancelDialogOpen(true);
          return;
        } else {
          const lt = updatedEventsToLog?.map(et => et.events).flat()
          setTimesheetEvents(lt);
          setSelectedEvents(updatedEventsToLog);
          const result = unSelectedEvents.filter(
            (element) => !parentEvents.includes(element)
          );
          unSelectedEventsRef.current = result;
          setUnSelectedEvents(result);
          return;
        }
      }
      unSelectedEventsRef.current = events;
      setUnSelectedEvents(events);
      let duration = totalEventsDurationInSec - Number(event?.duration);
      setTotalEventsDurationInSec(duration);
      setToggleSubEventsDuration(
        toggleSubEventsDuration - Number(event?.duration)
      );
    }
  };

  const isEventUnselected = (id) => {
    if (unSelectedEvents.find((e) => e === id)) {
      return false;
    } else {
      return true;
    }
  };

  const isValidForm = () => {
    let prevState = { ...props.timesheetToEdit };
    if (
      state?.title === prevState?.title &&
      state?.description === prevState?.description &&
      state?.projectId === prevState?.project?.id &&
      state?.billable === prevState?.billable &&
      taskId === prevState?.task?.id &&
      areEventListsIdentical(prevState?.tags, selectedTags)
    ) {
      return false;
    }

    if (!state.title || !state.projectId || !taskId) {
      return false;
    }
    if (!state.title) {
      return false;
    }
    if (!state.projectId) {
      return false;
    }
    if (!taskId) {
      return false;
    }
    // if (selectedTags?.length < 1) {
    //   setIsSubmit(false);
    //   return false;
    // }
    let totalDuration = 0;
    if (Number(state.hrs) > 0) {
      totalDuration = Number(state.hrs) * 60 * 60;
    }
    if (Number(state.mins) > 0) {
      totalDuration += Number(state.mins) * 60;
    }
    if (Number(state.secs) > 0) {
      totalDuration += Number(state.secs);
    }
    // if (totalDuration < 60) {
    //   return false;
    // }
    return true;
  };

  const handleSubmit = () => {
    if (isProjectRemoved) {
      toast.error(t("RAL0749"), {
        duration: 2000,
      });
      return;
    }
    if (!state.title) {
      setError({
        ...error,
        title: t("RAL0001"),
      });
      return false;
    }
    if (!state.projectId) {
      setError({
        ...error,
        projectId: t("RAL0001"),
      });
      return false;
    }
    if (!taskId) {
      setError({
        ...error,
        taskId: t("RAL0001"),
      });
      return false;
    }
    // if (selectedTags?.length < 1) {
    //   setError({
    //     ...error,
    //     tags: t("RAL0001"),
    //   });
    //   return false;
    // }
    let totalDuration = 0;
    if (Number(state.hrs) > 0) {
      totalDuration = Number(state.hrs) * 60 * 60;
    }
    if (Number(state.mins) > 0) {
      totalDuration += Number(state.mins) * 60;
    }
    if (Number(state.secs) > 0) {
      totalDuration += Number(state.secs);
    }

    // Both the code for filtering the events and calculating the total duration of all filtered events are combined here.
    let events = [];
    let eventTotalDuration = 0;
    selectedEvents?.forEach((e) => {
      if (e?.events?.length > 0) {
        let eventList = e?.events?.filter(
          (obj) => !unSelectedEvents.includes(obj.id) && !loggedEventsIds?.includes(obj.id)
        );
        let eventIds = eventList?.map((obj) => obj.id);
        let duration = eventList?.reduce((accumulator, currentValue) => {
          return accumulator + currentValue?.duration;
        }, 0);
        if (!duration) duration = e.duration;
        eventTotalDuration += duration;
        if (eventIds) events.push(...eventIds);
      } else {
        events.push(e?.id);
      }
    });

    let payload = {
      ...state,
      tags: selectedTags.length > 0 ? selectedTags : [],
      loggedDuration: eventTotalDuration,
      eventDuration: eventTotalDuration,
      events: events,
      taskId: taskId,
    };
    setIsSubmit(false);
    setIsLoading(true);
    updateTimesheet(state.id, payload).then((res) => {
      setIsLoading(false);
      if (res.data.code == "RCI0037") {
        toast.success(res.data?.message, {
          duration: 2000,
        });
        setTimesheetEvents([]);
        setTimesheetToEdit(undefined);
        setReload(true);
        setLogSubmited(true);
        SetSubEventSelected([]);
        setUnSelectedEvents([]);
      } else {
        toast.error(res.data?.message, {
          duration: 2000,
        });
      }
    });
  };
  const handleSubEventsToogle = (e, duration) => {
    if (toggleSubEvents?.id === e?.id) {
      setToggleSubEvents(null);
      setToggleSubEventsDuration(0);
    } else {
      setToggleSubEvents(e);
      setToggleSubEventsDuration(duration);
    }
  };
  const handleRemoveEvent = (id) => {

    setDurationModified(true);
    let events = selectedEvents.filter((e) => e.id !== id);
    let deselectedEvents = selectedEvents.filter((e) => e.id === id);
    selectedEventsRef.current = events;
    setSelectedEvents(events);
    let removedIds = deselectedEvents[0]?.events?.map(e => e.id);
    unSelectedEventsRef.current = unSelectedEvents?.filter(usId => !removedIds.includes(usId))
    setUnSelectedEvents(unSelectedEvents?.filter(usId => !removedIds.includes(usId)))
    SetSubEventSelected(subEventSelected?.filter(seId => !removedIds.includes(seId)))
    if (events.length < 1) {
      setTimesheetToEdit(undefined);
    }
    let allEvents = [];
    events.forEach((e) => {
      allEvents.push(...e.events);
    });
    setTimeout(() => {
      setTimesheetEvents(allEvents);
    }, [10]);
  };

  const textAreaRef = useRef(null);

  const handleDescriptionChange = (e) => {
    setState({ ...state, description: e.target.value });
    setIsModified(true);
    setError((prevError) => ({
      ...prevError,
      description: "",
    }));
  };

  useEffect(() => {
    if (textAreaRef.current) {
      textAreaRef.current.style.height = "auto";
      const { scrollHeight } = textAreaRef.current;
      const height = Math.max(40, Math.min(scrollHeight, 200));
      textAreaRef.current.style.height = `${height}px`;
    }
  }, [state.description]);

  const handleCancelButton = () => {
    let prevState = { ...props.timesheetToEdit };
    const isFormDataChanged =
      state?.title !== prevState?.title ||
      state?.description !== prevState?.description ||
      state?.projectId !== prevState?.project?.id ||
      state?.billable !== prevState?.billable ||
      taskId !== prevState?.task.id ||
      totalEvents !== prevState?.events?.length ||
      !areEventListsIdentical(prevState?.tags, selectedTags);
    if (isFormDataChanged) {
      setIsCancelDialogOpen(true);
    } else {
      setTimesheetEvents([]);
      setTimesheetToEdit(undefined);
      setReload(true);
      setLogSubmited(true);
      SetSubEventSelected([]);
      setUnSelectedEvents([]);
    }
  };

  const handleCloseCancelDialog = () => {
    setIsCancelDialogOpen(false);
    setEventCancelDialogOpen(false);
  };

  const handleConfirmCancel = () => {
    setIsCancelDialogOpen(false);
    setEventCancelDialogOpen(false);
    setTimesheetEvents([]);
    setTimesheetToEdit(undefined);
    SetSubEventSelected([])
    setUnSelectedEvents([])
  };

  const typographyStyle = {
    color: mode == "dark" ? "#F8F8F8" : "#242424",
    fontFamily: "Gotham",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    overflow: "hidden",
    width: "100%",
    opacity: "0.8",
    fontSize: "14px",
    lineHeight: "21px",
    padding: "2px 12px",
    cursor: "pointer",
  }

  return (
    <div
      className={css`
        min-height: calc(100% - 20px);
        position: relative;
      `}
    >
      <Box
        item
        className={`${css`
          padding-bottom: 20px;
          padding-top: 14px;
          align-items: center;
          display: flex;
        `} header-sticky`}
      >
        <Box flexGrow={1}>
          <Typography
            id="timesheet-worklog-text"
            variant="header3"
            sx={{
              color: "var( --text-secondary-80)",
              font: "normal normal bold 18px/20px Gotham",
              letterSpacing: "0px",
            }}
          >
            {t("RAL0571")}
          </Typography>
        </Box>
      </Box>

      <Box
        className={css`
          height: calc(89% - 20px);
          overflow: hidden;
          padding-right: 0px;
          :hover {
            overflow-y: auto;
            padding-right: 0px;
          }
        `}
      >
        <Box display={"flex"} flexDirection={"column"} mb={2} rowGap={2}>
          <Box item width={"100%"}>
            <CustomTextField
              id="textfield-timesheet-worklog-title"
              hideLabel={true}
              onChange={(e) => {
                setState({ ...state, title: e.target.value });
                setIsModified(true);
                setError({
                  ...error,
                  title: "",
                });
              }}
              placeholder={t("RAL0338")}
              required
              helperText={error?.title}
              error={error?.title}
              label={t("RAL0572")}
              value={state.title}
            // inputProps={{
            //   maxLength: 50,
            // }}
            />
          </Box>
          <Box item width={"100%"}>
            <CustomTextField
              id="textfield-timesheet-worklog-description"
              hideLabel={true}
              multiline
              rows={1}
              inputRef={textAreaRef}
              onChange={handleDescriptionChange}
              placeholder={t("RAL0292")}
              required
              helperText={error?.description}
              error={error?.description}
              label={t("RAL0292")}
              value={state.description}
              sx={{
                ".MuiInputBase-input.MuiOutlinedInput-input": {
                  padding: "0px !important",
                },
              }}
              inputProps={{
                maxLength: 2000,
              }}
            />
            <Box display={"flex"} flexDirection={"row-reverse"}>
              <Typography
                id="textfield-timesheet-worklog-description"
                variant="activity_log_count"
                sx={{
                  color: "var(--text-secondary-80-80)",
                }}
              >
                {state?.description?.length}/2000
              </Typography>
            </Box>
          </Box>
          <Box item width={"100%"} mt={-1.5}>

            <Autocomplete
              disabled={isProjectRemoved}
              error={!CommonUtil.isEmptyString(error.projectId)}
              helperText={error.projectId}
              id="dropdown-member-create-project"
              fullWidth
              size={"small"}
              getOptionLabel={(option) => option?.name || ""}
              clearText={t('RAL0693')}
              value={selectedProject}
              options={projects ? projects : []}
              noOptionsText={t("RAL0732")}
              renderInput={(params) => (
                <CustomTextField
                  id="dropdown-member-create-project"
                  hideLabel={true}
                  dropdown={true}
                  onChange={(e) => { }}
                  placeholder={t("RAL0288")}
                  required
                  helperText={error?.projectId}
                  error={error?.projectId}
                  {...params}
                  label={t("RAL0288") + " *"}
                />
              )}
              onChange={(event, newValue) => {
                if (newValue) {
                  setState({
                    ...state,
                    projectId: newValue.id,
                    tags: null,
                  });
                  setIsModified(true);
                } else {
                  setState({
                    ...state,
                    projectId: null,
                    tags: null,
                  });
                  setIsModified(true);
                  setSelectedProject(null);
                }
                setSelectedTags([]);
                setTaskId("");
                setError({
                  ...error,
                  projectId: "",
                  taskId: "",
                });
              }}
              renderOption={(props, option) => (
                <Box  {...props}>
                  <CustomTypography title={option.name}
                    component={{
                      typography: {
                        sx: typographyStyle
                      }
                    }}
                  >{option.name}</CustomTypography>
                </Box>
              )}
              componentsProps={commonComponentsProps(mode)}
              sx={{
                ".MuiInputBase-input.MuiOutlinedInput-input": {
                  paddingX: "10px !important",
                },
              }}
            />
          </Box>
          <Box item width={"100%"}>
            <Autocomplete
              disabled={!selectedProject || isProjectRemoved}
              size="small"
              fullWidth
              id="task-outlined"
              options={isProjectRemoved ? [archivedProjectTask] : taskList ? taskList : []}
              getOptionLabel={(option) => option?.taskId + " " + option?.title || ""}
              noOptionsText={t("RAL1164")}
              value={isProjectRemoved ? archivedProjectTask : taskList.find((task) => task.id === taskId) || null}
              onChange={(e, newTask) => {
                if (newTask !== null)
                  setTaskId(newTask.id);
                else setTaskId("");
                setIsModified(true);
                setError({ ...error, taskId: "" });
              }}
              error={!CommonUtil.isEmptyString(error.taskId)}
              helperText={error.taskId}
              componentsProps={commonComponentsProps(mode)}
              sx={{
                ".MuiInputBase-input.MuiOutlinedInput-input": {
                  paddingX: "10px !important",
                },
              }}
              renderInput={(params) => {
                const selectedTask = taskList.find((task) => task.id === taskId);
                const taskLabel = selectedTask?.length > 0
                  ? `${selectedTask.taskId} ${selectedTask.title}`.trim()
                  : `${archivedProjectTask.taskId} ${archivedProjectTask.title}`.trim();
                return (
                  <Tooltip
                    title={taskLabel}
                    disableHoverListener={!taskLabel || taskLabel.length < 22}
                  >
                    <div>
                      <CustomTextField
                        dropdown={true}
                        hideLabel={true}
                        onChange={(e) => { }}
                        required
                        helperText={error?.taskId}
                        error={error?.taskId}
                        value={selectedTask?.length > 0 ? taskId : archivedProjectTask.id}
                        {...params}
                        label={t("RAL1123") + " *"}
                        sx={{
                          // "& .MuiOutlinedInput-root": {
                          //   backgroundColor: "var(--background-color-3) !important",
                          // },
                          "& .MuiInputBase-input": {
                            textOverflow: "ellipsis",
                            overflow: "hidden",
                            whiteSpace: "nowrap",
                          },
                        }}
                      />
                    </div>
                  </Tooltip>
                );
              }}
              renderOption={(props, option) => {
                if (option.status === "ARCHIVED" || option.status === "COMPLETED") {
                  return null
                } else
                  return (
                    <Box  {...props}>
                      <Chip
                        id="tags-outlined"
                        key={option.id}
                        label={option.taskId}
                      />
                      <CustomTypography title={option.title}
                        component={{
                          typography: {
                            sx: typographyStyle
                          }
                        }}
                      >{option.title}</CustomTypography>
                    </Box>
                  )
              }}
            />
          </Box>
          {selectedProject && !isProjectRemoved && (
            <span
              className={mode === "dark" ? "worklog-add-tag-dark" : "worklog-add-tag"}
              onClick={() => setIsTaskPopupOpen(true)}
            >
              {t("RAL1124")}
            </span>
          )}
          <Box item width={"100%"}>
            <Autocomplete
              disabled={!selectedProject || isProjectRemoved}
              multiple
              disableCloseOnSelect={true}
              id="tags-outlined"
              options={selectedProject?.tags ? selectedProject?.tags : []}
              getOptionLabel={(option) => option.name}
              filterSelectedOptions
              noOptionsText={t("RAL0720")}
              ChipProps={{
                sx: {
                  backgroundColor: "var(--popover-background) !important",
                  color: "var(--text-secondary-80)",
                  borderRadius: "4px",
                  "& .MuiChip-deleteIcon": {
                    color: "var(--text-secondary-50)",
                  },
                },
              }}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    id="tags-outlined"
                    key={option.id}
                    label={option.name}
                    icon={
                      option.iconUrl ? (
                        <img
                          src={option.iconUrl}
                          alt={option.name}
                          height={18}
                          width={18}
                        />
                      ) : (
                        <TagInactiveIcon
                          id="timesheet-worklog-tagInactiveIcon"
                          height={17.99}
                          width={17.99}
                        />
                      )
                    } // Dynamically set the icon
                    {...getTagProps({ index })}
                    deleteIcon={
                      <CancelIcon id="timesheet-worklog-cancelicon" />
                    }
                  />
                ))
              }
              value={selectedTags}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={tagSelected}
              renderOption={(props, option) => (
                <Box
                  component="li"
                  sx={{ "& > span": { mr: 2, flexShrink: 0 } }}
                  {...props}
                  key={option.id}
                >
                  <Box
                    sx={{
                      width: "100%",
                      height: "auto",
                      wordBreak: "break-word",
                      whiteSpace: "normal",
                      display: "inline",
                    }}
                  >
                    <Box display={"flex"} columnGap={1} alignItems={"center"}>
                      {option?.iconUrl ? (
                        <img
                          id="tags-outlined"
                          src={option?.iconUrl}
                          alt={option.name}
                          height={17.99}
                          width={17.99}
                        />
                      ) : (
                        <TagInactiveIcon
                          id="timesheet-worklog-tagInactiveIcon"
                          height={17.99}
                          width={17.99}
                        />
                      )}

                      <Box>{option.name}</Box>
                    </Box>
                  </Box>
                </Box>
              )}
              componentsProps={commonComponentsProps(mode)}
              sx={{
                "& .MuiOutlinedInput-root": {
                  background: tagError
                    ? "#FFF1F1 0% 0% no-repeat padding-box"
                    : "#F4F5F8 0% 0% no-repeat padding-box",
                  "& fieldset": {
                    borderRadius: "none",
                    border: "none",
                  },
                  ".MuiInputBase-input.MuiOutlinedInput-input": {
                    paddingY: "4px !important",
                    paddingX: "5px !important",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "none",
                  },
                  "&:hover fieldset": {
                    borderColor: "none",
                  },
                  "& .MuiInputLabel-root ": {
                    display: "none",
                    border: "none",
                  },
                },
                "& .MuiChip-root": {
                  // background: "#FFFFFF 0% 0% no-repeat padding-box",
                  borderRadius: "4px",
                },
              }}
              renderInput={(params) => (
                <CustomTextField
                  dropdown={true}
                  hideLabel={true}
                  onChange={(e) => { }}
                  required
                  helperText={error?.tags}
                  error={error?.tags}
                  {...params}
                  label={t("RAL0191")}
                  inputProps={{
                    ...params.inputProps,
                    maxLength: 20
                  }}
                />
              )}
            />
          </Box>
          {/* {(isCreatingTag || isCreatingTask) && (
            <Box
              sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 9999,
              }}
            >
              <CircularProgress />
            </Box>
          )} */}
          {CommonUtil.isOwnerOrAdminOrManager() && selectedProject
            && !isProjectRemoved && (
              <span
                className={mode === "dark" ? "worklog-add-tag-dark" : "worklog-add-tag"}
                onClick={() => setShowTagInput(true)}
                style={{ display: showTagInput ? 'none' : 'block' }}
              >
                {t("RAL1196")}
              </span>
            )}
          {
            showTagInput && selectedProject && (
              <div>
                <Box className="project-tags-add-wra45435pper">
                  <Box
                    display={"flex"}
                    alignItems={"center"}
                    className="new-tag"
                    justifyContent={"center"}
                    flexDirection={"row-reverse"}
                  >
                    <Box
                      display={"flex"}
                      flexGrow={1}
                      alignItems={"center"}
                      className="tag-name"
                    >
                      <Box item flexGrow={1}>
                        <ProjectTagTextField
                          id="textfield-tags-add"
                          label={t("RAL0194")}
                          error={!!error}
                          helperText={error}
                          value={newTag.name}
                          onChange={(e) => handleAddChange(e)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              handleAddTag();
                            }
                          }}
                          onClick={handleAddTag}
                          onCancelClick={resetOnAdd}
                          inputProps={{
                            maxLength: 20,
                          }}
                          disabled={isCreatingTag}
                        />
                      </Box>
                    </Box>
                  </Box>
                </Box>

              </div>
            )}
          {/* <Box
            display={"flex"}
            pl={2}
            py={1}
            bgcolor={"var(--background-color-12)"}
            alignItems={"center"}
          >
            <Box item flexGrow={1}>
              <Typography
                id="timesheet-worklog-totalloggedtime"
                className="worklog-time"
              >
                {t("RAL0574")}
              </Typography>
            </Box>
            <Box item>
              <CustomTextField
                disabled={true}
                id="textfield-timesheet-worklog-hrs"
                label=""
                value={state?.hrs}
                onChange={(e) => {
                  if (Number(e.target.value <= 24)) {
                    let min =
                      Number(e.target.value) === 24 ? Number(0) : state.mins;
                    setState({
                      ...state,
                      hrs: Number(e.target.value),
                      mins: min,
                    });
                    setIsModified(true);
                    setError({ ...error, loggedDuration: "" });
                  }
                }}
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root ": {
                    backgroundColor: "var(--background-color-3) !important",
                    height: "40px",
                    width: "40px",
                  },
                  ".MuiInputBase-input.MuiOutlinedInput-input": {
                    textAlign: "center",
                    padding: "0px !important",
                  },
                }}
              />
            </Box>
            <Box item px={1} mt={1}>
              <Typography
                id="textfield-timesheet-worklog-hrs"
                className="worklog-time"
              >
                h
              </Typography>
            </Box>
            <Box item>
              <CustomTextField
                disabled={true}
                id="textfield-timesheet-worklog-mins"
                label=""
                value={state?.mins}
                onChange={(e) => {
                  if (Number(e.target.value <= 60) && state.hrs !== 24) {
                    setState({ ...state, mins: Number(e.target.value) });
                    setIsModified(true);
                    setError({ ...error, loggedDuration: "" });
                  }
                }}
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root ": {
                    backgroundColor: "var(--background-color-3) !important",
                    height: "40px",
                    width: "40px",
                  },
                  ".MuiInputBase-input.MuiOutlinedInput-input": {
                    textAlign: "center",
                    padding: "0px !important",
                  },
                }}
              />
            </Box>
            <Box item px={1} mt={1}>
              <Typography
                id="textfield-timesheet-worklog-mins"
                className="worklog-time"
              >
                m
              </Typography>
            </Box>
          </Box> */}

          {error?.loggedDuration && (
            <Box display={"flex"} alignItems={"center"}>
              <Box item pr={1}>
                {<ErrorIcon id="timesheet-worklog-erroricon" />}
              </Box>
              <Box item>
                <Typography
                  id="timesheet-worklog-errormessage"
                  className="helper-text"
                >
                  {error?.loggedDuration}
                </Typography>
              </Box>
            </Box>
          )}
          <Box
            className={css`
              background: var(--background-color-13) 0% 0% no-repeat padding-box;
              border-radius: 5px;
              opacity: 1;
              display: flex;
              padding: 12px;
              align-items: center;
            `}
          >
            <Box item flexGrow={1}>
              <Typography
                id="timesheet-worklog-billable"
                className={css`
                  font: normal normal normal 16px/20px Gotham;
                  letter-spacing: 0px;
                  color: var(--text-secondary-50);
                  opacity: 1;
                `}
              >
                {t("RAL0341")}
              </Typography>
            </Box>
            <Box item>
              <CustomSwitch
                id="timesheet-worklog-switch"
                checked={state.billable}
                onChange={(e) => {
                  setState({ ...state, billable: e.target.checked });
                  setIsModified(true);
                }}
              />
            </Box>
          </Box>
          <Box
            item
            className={css`
              alignitems: center;
              display: flex;
            `}
          >
            <Box flexGrow={1}>
              <Typography
                variant="header3"
                id="timesheet-worklog-events"
                sx={{
                  color: "var( --text-secondary-80)",
                  font: "normal normal bold 18px/20px Gotham",
                  letterSpacing: "0px",
                }}
              >
                {t("RAL0048")}
              </Typography>
              <Box
                sx={{
                  marginLeft: "8px",
                  display: "inline-block",
                  width: "30px",
                  height: "20px",
                  background: "var(--background-color-33)",
                  textAlign: "center",
                }}
              >
                <Typography
                  variant="header3"
                  sx={{
                    verticalAlign: "text-top",
                    color: "var(--text-color-4)",
                    font: "normal normal medium 12px/15px Gotham",
                  }}
                >
                  {totalEvents}
                </Typography>
              </Box>
            </Box>
            <Box>
              {(totalEventsDuration?.hr !== undefined) && <Typography
                variant="header3"
                sx={{
                  color: "var( --text-secondary-80)",
                  font: "normal normal medium 16px/19px Gotham",
                  letterSpacing: "0px",
                }}
              >
                {totalEventsDuration?.hr +
                  "h " +
                  totalEventsDuration?.min +
                  "m " +
                  totalEventsDuration?.sec +
                  "s"}
              </Typography>}
            </Box>
          </Box>
          <Box
            className="fadeLeft"
            rowGap={1}
            display={"flex"}
            flexDirection={"column"}
          >
            {selectedEvents?.map((e) => {
              return (
                <EventList
                  editable={true}
                  event={e}
                  onRemove={() => handleRemoveEvent(e.id)}
                  isEventUnselected={(id) => isEventUnselected(id)}
                  onRemoveSubevent={(se) => handleUnselectEvent(se, e)}
                  setEventToEdit={(e) => setEventToEdit(e)}
                  onTitleUpdate={() => handleEventUpdate(e)}
                  selectedEvents={selectedEvents}
                  type="editWorklog"
                  totalEvents={totalEvents}
                />
              );
            })}
          </Box>
        </Box>
      </Box>
      <Box
        className="btnFixedScroll"
        display={"flex"}
        flexDirection={"row-reverse"}
        py={2}
      >
        <Box item pl={1} width={"50%"}>
          <CustomButton
            disabled={!isSubmit}
            variant="contained"
            id="btn-timesheet-add"
            size="large"
            fullWidth="fullWidth"
            onClick={() => handleSubmit()}
          >
            <span id="btn-timesheet-add">{t("RAL0342")}</span>
          </CustomButton>
        </Box>
        <Box item width={"50%"} pr={1}>
          <CustomButton
            variant="outlined"
            id="btn-timesheet-cancel"
            size="large"
            fullWidth="fullWidth"
            onClick={handleCancelButton}
          >
            <span id="btn-timesheet-cancel">{t("RAL0177")}</span>
          </CustomButton>
        </Box>
      </Box>

      {isCancelDialogOpen && (<CancelDialog
        open={isCancelDialogOpen}
        onClose={handleCloseCancelDialog}
        onConfirm={handleConfirmCancel}
        header={t("RAL0393")}
        message={t("RAL0394")}
      />)}

      {eventCancelDialogOpen && (
        <CancelDialog
          open={eventCancelDialogOpen}
          onClose={handleCloseCancelDialog}
          onConfirm={handleConfirmCancel}
          header={t("RAL0740")}
          message={t("RAL0741")}
        />
      )}
      {
        isTaskPopupOpen && (
          <TaskPopup
            open={isTaskPopupOpen}
            onClose={handleCloseTaskPopup}
            onTaskCreated={handleTaskCreated}
            selectedProject={selectedProject}
            isLoading={isCreatingTask}
          />
        )
      }
      <Box
        sx={{
          display: isLoading ? "flex" : "none",
          zIndex: 999,
        }}
      >
        <CircularProgress
          style={{ top: "50%", left: "85%", position: "fixed" }}
        />
      </Box>
    </div>
  );
}
